import { clone } from "../utils/CoreUtils";
import { clearAllCaches } from "./Caching";
import { hasGlobalFlag, setGlobalFlag } from "./Flags";
import { PlayerContextGlobalState, PlayerContextState } from "./PlayerContext";
import { addContextListener } from "./PlayerContextListeners";
import { Resource } from "./Resources";
import { retire } from "./Retire";
import {
  TransformationType,
  registerTransformation,
} from "./calculation/Calculation";
import { TransformationTags } from "./calculation/TransformationTags";
import { hasEverTriggeredEnding } from "./endings/Endings";
import { EventTag } from "./events/GameEvent";
import { AttackTarget } from "./exploration/AttackTarget";
import { CombatStat } from "./exploration/CombatStats";

const NEW_GAME_PLUS_RESET_FIELDS: Partial<PlayerContextGlobalState> = {
  flags: {},
  maxPrimarySchoolLevels: {},
  maxSchoolLevels: {},
  unlockedElements: { Fire: true, Earth: true, Water: true, Air: true },
  unlockedSchools: {
    Conjuration: true,
    Enchantment: true,
    Illusion: true,
    Alchemy: false,
    Evocation: false,
    Protection: false,
    Breeding: false,
    Summoning: false,
    Divination: false,
  },
  researchersHiredLastRun: 0,
  timeCapsulesLastRun: 0,
  lastDungeonFloorCompletedLastRun: "none",
  totalTimesResetted: 0,
  endingsTriggered: [],
  autocastLoadouts: [],
  quickbars: [],
  currentQuickbar: 0,
  timesEnemyDefeated: {},
  thisWorldSecondsPlayed: 0,
  thisWorldActiveSecondsPlayed: 0,
  overrideElementDisplay: "no",
  notifications: [],
};

function newGamePlusReset(state: PlayerContextState): PlayerContextState {
  state = retire(state, false);

  // Increment tick count to invalidate caches
  state.global.tickCount++;
  clearAllCaches();

  const newGlobalState = clone(state.global);
  for (let field in NEW_GAME_PLUS_RESET_FIELDS) {
    // @ts-ignore Checking manually
    newGlobalState[field] = clone(NEW_GAME_PLUS_RESET_FIELDS[field]);
  }
  newGlobalState.thisWorldStartTimestamp = Date.now();
  // Disable early game popup
  newGlobalState.hideEventTags.filter((tag) => tag != EventTag.EARLY_GAME);
  state.global = newGlobalState;
  return state;
}

export const MAX_BUFF_DIFFICULTY_LEVEL = 100;

export function getMaxPossibleBuffAndDifficultyLevel(
  state: PlayerContextState,
): number {
  return Math.min(
    state.global.newGamePlusMax +
      (hasEverTriggeredEnding(state, "standard_ending") ? 5 : 0),
    MAX_BUFF_DIFFICULTY_LEVEL,
  );
}

export function triggerNewGamePlus(
  state: PlayerContextState,
  newGamePlusBuffLevel: number,
  newGamePlusDifficultyLevel: number,
): PlayerContextState {
  state.global.newGamePlusCount++;
  state.global.newGamePlusMax = getMaxPossibleBuffAndDifficultyLevel(state);
  newGamePlusBuffLevel = Math.min(
    newGamePlusBuffLevel,
    state.global.newGamePlusMax,
  );
  newGamePlusDifficultyLevel = Math.min(
    newGamePlusDifficultyLevel,
    state.global.newGamePlusMax,
  );
  state.global.newGamePlusBuffLevel = newGamePlusBuffLevel;
  state.global.newGamePlusDifficultyLevel = newGamePlusDifficultyLevel;
  // This is repeated twice for the following reason:
  // 1. Retirement must be done after resetting the status
  // 2. But retirement also modifies global state, so it needs to be reset
  state = newGamePlusReset(newGamePlusReset(state));
  return state;
}

export function getNewGamePlusCount(state: PlayerContextState): number {
  return state.global.newGamePlusCount;
}

export function getNewGamePlusBuffLevel(state: PlayerContextState): number {
  return state.global.newGamePlusBuffLevel;
}

export function getNewGamePlusDifficultyLevel(
  state: PlayerContextState,
): number {
  return state.global.newGamePlusDifficultyLevel;
}

export function getNewGamePlusSchoolLevelBonus(buffLevel: number): number {
  return buffLevel * 2;
}

export function getNewGamePlusResearchMultiplier(buffLevel: number): number {
  return Math.pow(1.12, buffLevel);
}

export function getNewGamePlusProductionMultiplier(buffLevel: number): number {
  return Math.pow(1.03, buffLevel);
}

export function getNewGamePlusCostScaleMultiplier(buffLevel: number): number {
  return Math.pow(0.99, buffLevel);
}

export function getNewGamePlusStorageMultiplier(buffLevel: number): number {
  return Math.pow(1.12, buffLevel);
}

export function getNewGamePlusHpMultiplier(difficultyLevel: number): number {
  return Math.pow(1.075, difficultyLevel);
}

export function getNewGamePlusAttackDelayMultiplier(
  difficultyLevel: number,
): number {
  return Math.pow(0.985, difficultyLevel);
}

export function getNewGamePlusAccuracyMultiplier(
  difficultyLevel: number,
): number {
  return Math.pow(1.02, difficultyLevel);
}

export function getNewGamePlusOtherStatMultiplier(
  difficultyLevel: number,
): number {
  return Math.pow(1.035, difficultyLevel);
}

export function loadNewGamePlus() {
  // Backend transformations related to New Game Plus
  registerTransformation(
    [[TransformationTags.MaxSchoolLevel]],
    "NewGamePlusMaxSchoolLevel",
    TransformationType.Addition,
    (state) => {
      return getNewGamePlusSchoolLevelBonus(getNewGamePlusBuffLevel(state));
    },
  );

  registerTransformation(
    [[TransformationTags.Research]],
    "NewGamePlusResearch",
    TransformationType.Multiplier,
    (state) => getNewGamePlusResearchMultiplier(getNewGamePlusBuffLevel(state)),
  );

  registerTransformation(
    [[TransformationTags.Production]],
    "NewGamePlusProduction",
    TransformationType.Multiplier,
    (state) =>
      getNewGamePlusProductionMultiplier(getNewGamePlusBuffLevel(state)),
  );

  registerTransformation(
    [[TransformationTags.Loot]],
    "NewGamePlusLoot",
    TransformationType.Multiplier,
    (state) =>
      getNewGamePlusProductionMultiplier(getNewGamePlusBuffLevel(state)),
  );

  registerTransformation(
    [[TransformationTags.CostScale, "Category:Buildings"]],
    "NewGamePlusCostScale",
    TransformationType.Multiplier,
    (state, params) =>
      getNewGamePlusCostScaleMultiplier(getNewGamePlusBuffLevel(state)),
  );

  for (let resource in Resource) {
    if (resource != Resource.Mana && resource != Resource.TimePieces) {
      registerTransformation(
        [[resource, TransformationTags.Cap]],
        resource + "NewGamePlusStorage",
        TransformationType.Multiplier,
        (state) =>
          getNewGamePlusStorageMultiplier(getNewGamePlusBuffLevel(state)),
      );
    }
  }

  registerTransformation(
    [[AttackTarget.Enemy, CombatStat.MaxHP]],
    "NewGamePlusEnemyStatsHP",
    TransformationType.Multiplier,
    (state) => getNewGamePlusHpMultiplier(getNewGamePlusDifficultyLevel(state)),
  );

  registerTransformation(
    [[AttackTarget.Enemy, CombatStat.AttackDelay]],
    "NewGamePlusAttackDelay",
    TransformationType.Multiplier,
    (state) =>
      getNewGamePlusAttackDelayMultiplier(getNewGamePlusDifficultyLevel(state)),
  );

  registerTransformation(
    [
      [AttackTarget.Enemy, CombatStat.Attack],
      [AttackTarget.Enemy, CombatStat.Defense],
      [AttackTarget.Enemy, CombatStat.Dodge],
    ],
    "NewGamePlusEnemyStats",
    TransformationType.Multiplier,
    (state) =>
      getNewGamePlusOtherStatMultiplier(getNewGamePlusDifficultyLevel(state)),
  );

  registerTransformation(
    [[AttackTarget.Enemy, CombatStat.Accuracy]],
    "NewGamePlusEnemyAccuracy",
    TransformationType.Multiplier,
    (state) =>
      getNewGamePlusAccuracyMultiplier(getNewGamePlusDifficultyLevel(state)),
  );

  addContextListener("NewGamePlusCreationUnlockerForLater", (state) => {
    if (
      !hasGlobalFlag(state, "newgameplus_unlocked") &&
      getNewGamePlusCount(state) > 0
    ) {
      return setGlobalFlag("newgameplus_unlocked");
    }
  });
}
